/**
 * Pattern Analytics Service - Phase 4.1
 * Advanced pattern analysis with trend detection, correlation tracking, and insights generation
 */

class PatternAnalyticsService {
  constructor() {
    this.analysisCache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Comprehensive pattern analysis for a user
   */
  async analyzeUserPatterns(userId, timeRange = 30) {
    const cacheKey = `${userId}-${timeRange}`;
    const cached = this.analysisCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    try {
      const patterns = this.getUserPatterns(userId);
      const analysis = {
        userId,
        timeRange,
        generatedAt: new Date().toISOString(),
        
        // Core pattern analysis
        productivity: await this.analyzeProductivityPatterns(patterns, timeRange),
        communication: await this.analyzeCommunicationPatterns(patterns, timeRange),
        coordination: await this.analyzeCoordinationPatterns(patterns, timeRange),
        environmental: await this.analyzeEnvironmentalPatterns(patterns, timeRange),
        goal_achievement: await this.analyzeGoalAchievementPatterns(patterns, timeRange),
        
        // Advanced insights
        correlations: await this.findPatternCorrelations(patterns),
        trends: await this.detectTrends(patterns, timeRange),
        recommendations: await this.generateAnalyticsRecommendations(patterns),
        
        // Metadata
        dataQuality: this.assessDataQuality(patterns),
        confidence: this.calculateAnalysisConfidence(patterns, timeRange)
      };

      // Cache the results
      this.analysisCache.set(cacheKey, {
        data: analysis,
        timestamp: Date.now()
      });

      return analysis;
    } catch (error) {
      console.error('Error analyzing user patterns:', error);
      return this.getEmptyAnalysis(userId, timeRange);
    }
  }

  /**
   * Analyze productivity patterns with peak detection and efficiency scoring
   */
  async analyzeProductivityPatterns(patterns, timeRange) {
    const productivityData = patterns.filter(p => 
      p.action === 'task-completed' || 
      p.action === 'energy-level-set' ||
      p.action === 'focus-session'
    );

    if (productivityData.length < 5) {
      return this.getEmptyProductivityAnalysis();
    }

    // Peak hours analysis
    const hourlyProductivity = this.analyzeHourlyProductivity(productivityData);
    const peakHours = this.detectProductivityPeaks(hourlyProductivity);
    
    // Energy level correlation
    const energyCorrelation = this.analyzeEnergyProductivityCorrelation(productivityData);
    
    // Task completion patterns
    const completionPatterns = this.analyzeTaskCompletionPatterns(productivityData);
    
    // Productivity trends
    const trends = this.detectProductivityTrends(productivityData, timeRange);

    return {
      peakHours,
      energyCorrelation,
      completionPatterns,
      trends,
      hourlyDistribution: hourlyProductivity,
      averageProductivityScore: this.calculateAverageProductivity(productivityData),
      recommendations: this.generateProductivityRecommendations(peakHours, energyCorrelation, trends)
    };
  }

  /**
   * Analyze communication patterns and timing preferences
   */
  async analyzeCommunicationPatterns(patterns, timeRange) {
    const commData = patterns.filter(p => 
      p.action === 'family-communication' || 
      p.action === 'message-sent' ||
      p.action === 'suggestion-accepted'
    );

    if (commData.length < 3) {
      return this.getEmptyCommunicationAnalysis();
    }

    // Response time analysis
    const responsePatterns = this.analyzeResponseTimes(commData);
    
    // Preferred communication windows
    const preferredWindows = this.detectCommunicationWindows(commData);
    
    // Communication effectiveness
    const effectiveness = this.analyzeCommunicationEffectiveness(commData);

    return {
      responsePatterns,
      preferredWindows,
      effectiveness,
      averageResponseTime: this.calculateAverageResponseTime(commData),
      peakCommunicationHours: this.detectPeakCommunicationHours(commData),
      recommendations: this.generateCommunicationRecommendations(preferredWindows, effectiveness)
    };
  }

  /**
   * Analyze family coordination patterns and success rates
   */
  async analyzeCoordinationPatterns(patterns, timeRange) {
    const coordData = patterns.filter(p => 
      p.action === 'coordination-success' || 
      p.action === 'coordination-conflict' ||
      p.action === 'family-planning'
    );

    const coordinationScore = this.calculateCoordinationSuccessRate(coordData);
    const conflictPatterns = this.analyzeConflictPatterns(coordData);
    const planningEfficiency = this.analyzePlanningEfficiency(coordData);

    return {
      successRate: coordinationScore,
      conflictPatterns,
      planningEfficiency,
      optimalPlanningWindows: this.detectOptimalPlanningWindows(coordData),
      recommendations: this.generateCoordinationRecommendations(coordinationScore, conflictPatterns)
    };
  }

  /**
   * Analyze environmental preferences and weather correlations
   */
  async analyzeEnvironmentalPatterns(patterns, timeRange) {
    const envData = patterns.filter(p => 
      p.action === 'weather-activity-correlation' || 
      p.action === 'outdoor-activity-preference' ||
      p.metadata?.weather
    );

    const weatherPreferences = this.analyzeWeatherPreferences(envData);
    const seasonalPatterns = this.analyzeSeasonalPatterns(envData);
    const activityWeatherCorrelation = this.analyzeActivityWeatherCorrelation(envData);

    return {
      weatherPreferences,
      seasonalPatterns,
      activityWeatherCorrelation,
      optimalOutdoorConditions: this.detectOptimalOutdoorConditions(envData),
      recommendations: this.generateEnvironmentalRecommendations(weatherPreferences, seasonalPatterns)
    };
  }

  /**
   * Analyze goal achievement patterns and success factors
   */
  async analyzeGoalAchievementPatterns(patterns, timeRange) {
    const goalData = patterns.filter(p => 
      p.action === 'goal-completed' || 
      p.action === 'task-completed' ||
      p.action === 'milestone-achieved'
    );

    const achievementRate = this.calculateGoalAchievementRate(goalData);
    const successFactors = this.identifySuccessFactors(goalData);
    const timeToCompletion = this.analyzeTimeToCompletion(goalData);

    return {
      achievementRate,
      successFactors,
      timeToCompletion,
      optimalGoalSettings: this.detectOptimalGoalSettings(goalData),
      recommendations: this.generateGoalRecommendations(achievementRate, successFactors)
    };
  }

  /**
   * Find correlations between different pattern types
   */
  async findPatternCorrelations(patterns) {
    const correlations = {};

    // Productivity vs Energy correlation
    correlations.productivityEnergy = this.calculateCorrelation(
      patterns.filter(p => p.action === 'task-completed'),
      patterns.filter(p => p.action === 'energy-level-set'),
      'productivity',
      'energy'
    );

    // Weather vs Activity correlation
    correlations.weatherActivity = this.calculateCorrelation(
      patterns.filter(p => p.metadata?.weather),
      patterns.filter(p => p.action === 'activity-completed'),
      'weather',
      'activity'
    );

    // Communication vs Coordination correlation
    correlations.communicationCoordination = this.calculateCorrelation(
      patterns.filter(p => p.action === 'family-communication'),
      patterns.filter(p => p.action === 'coordination-success'),
      'communication',
      'coordination'
    );

    return correlations;
  }

  /**
   * Detect trends in user patterns over time
   */
  async detectTrends(patterns, timeRange) {
    const trends = {};

    // Productivity trends
    trends.productivity = this.detectProductivityTrends(patterns, timeRange);
    
    // Engagement trends
    trends.engagement = this.detectEngagementTrends(patterns, timeRange);
    
    // Success rate trends
    trends.successRate = this.detectSuccessRateTrends(patterns, timeRange);

    return trends;
  }

  /**
   * Generate analytics-based recommendations
   */
  async generateAnalyticsRecommendations(patterns) {
    const recommendations = [];

    // Productivity optimization recommendations
    const productivityRecs = this.generateProductivityOptimizationRecs(patterns);
    recommendations.push(...productivityRecs);

    // Communication timing recommendations
    const communicationRecs = this.generateCommunicationTimingRecs(patterns);
    recommendations.push(...communicationRecs);

    // Goal achievement recommendations
    const goalRecs = this.generateGoalAchievementRecs(patterns);
    recommendations.push(...goalRecs);

    return recommendations.sort((a, b) => b.confidence - a.confidence).slice(0, 5);
  }

  // Helper methods for pattern analysis

  analyzeHourlyProductivity(data) {
    const hourlyData = {};
    for (let hour = 0; hour < 24; hour++) {
      hourlyData[hour] = { count: 0, success: 0, energy: 0 };
    }

    data.forEach(item => {
      const hour = new Date(item.timestamp).getHours();
      if (hourlyData[hour]) {
        hourlyData[hour].count++;
        if (item.metadata?.success) hourlyData[hour].success++;
        if (item.metadata?.energyLevel) {
          const energyScore = this.energyLevelToScore(item.metadata.energyLevel);
          hourlyData[hour].energy += energyScore;
        }
      }
    });

    // Calculate productivity scores
    Object.keys(hourlyData).forEach(hour => {
      const data = hourlyData[hour];
      if (data.count > 0) {
        data.successRate = data.success / data.count;
        data.averageEnergy = data.energy / data.count;
        data.productivityScore = (data.successRate * 0.6) + (data.averageEnergy * 0.4);
      }
    });

    return hourlyData;
  }

  detectProductivityPeaks(hourlyData) {
    const hours = Object.keys(hourlyData)
      .map(hour => ({
        hour: parseInt(hour),
        score: hourlyData[hour].productivityScore || 0,
        count: hourlyData[hour].count
      }))
      .filter(h => h.count >= 2) // Need at least 2 data points
      .sort((a, b) => b.score - a.score);

    return {
      primary: hours[0] || null,
      secondary: hours[1] || null,
      peakWindow: this.identifyPeakWindow(hours)
    };
  }

  analyzeEnergyProductivityCorrelation(data) {
    const energyData = data.filter(d => d.metadata?.energyLevel);
    if (energyData.length < 3) return { correlation: 0, confidence: 0 };

    const energyScores = energyData.map(d => this.energyLevelToScore(d.metadata.energyLevel));
    const productivityScores = energyData.map(d => d.metadata?.success ? 1 : 0);

    const correlation = this.calculatePearsonCorrelation(energyScores, productivityScores);
    
    return {
      correlation,
      confidence: Math.min(energyData.length / 10, 1),
      optimalEnergyLevel: this.findOptimalEnergyLevel(energyData)
    };
  }

  energyLevelToScore(level) {
    const scores = { chill: 0.3, normal: 0.6, energetic: 1.0 };
    return scores[level] || 0.5;
  }

  calculatePearsonCorrelation(x, y) {
    if (x.length !== y.length || x.length === 0) return 0;

    const n = x.length;
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
    const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0);
    const sumY2 = y.reduce((sum, yi) => sum + yi * yi, 0);

    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

    return denominator === 0 ? 0 : numerator / denominator;
  }

  getUserPatterns(userId) {
    try {
      const patterns = JSON.parse(localStorage.getItem(`userPatterns_${userId}`) || '[]');
      return Array.isArray(patterns) ? patterns : [];
    } catch {
      return [];
    }
  }

  assessDataQuality(patterns) {
    const quality = {
      totalDataPoints: patterns.length,
      timeSpan: this.calculateTimeSpan(patterns),
      actionDiversity: this.calculateActionDiversity(patterns),
      dataCompleteness: this.calculateDataCompleteness(patterns)
    };

    quality.overallScore = (
      Math.min(quality.totalDataPoints / 50, 1) * 0.3 +
      Math.min(quality.timeSpan / 30, 1) * 0.2 +
      quality.actionDiversity * 0.3 +
      quality.dataCompleteness * 0.2
    );

    return quality;
  }

  calculateAnalysisConfidence(patterns, timeRange) {
    const dataQuality = this.assessDataQuality(patterns);
    return Math.min(dataQuality.overallScore * 1.2, 1);
  }

  // Empty analysis templates
  getEmptyAnalysis(userId, timeRange) {
    return {
      userId,
      timeRange,
      generatedAt: new Date().toISOString(),
      productivity: this.getEmptyProductivityAnalysis(),
      communication: this.getEmptyCommunicationAnalysis(),
      coordination: { successRate: 0, recommendations: [] },
      environmental: { weatherPreferences: {}, recommendations: [] },
      goal_achievement: { achievementRate: 0, recommendations: [] },
      correlations: {},
      trends: {},
      recommendations: [],
      dataQuality: { overallScore: 0 },
      confidence: 0
    };
  }

  getEmptyProductivityAnalysis() {
    return {
      peakHours: { primary: null, secondary: null },
      energyCorrelation: { correlation: 0, confidence: 0 },
      completionPatterns: {},
      trends: {},
      averageProductivityScore: 0,
      recommendations: []
    };
  }

  getEmptyCommunicationAnalysis() {
    return {
      responsePatterns: {},
      preferredWindows: [],
      effectiveness: 0,
      averageResponseTime: 0,
      recommendations: []
    };
  }

  // Additional helper methods would be implemented here...
  // (Truncated for brevity - full implementation would include all analysis methods)
}

export const patternAnalyticsService = new PatternAnalyticsService();